import axios from 'axios';
import { isInLoginGracePeriod } from './api';

const zoneAPI = axios.create({
  baseURL: '/api/v1',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para adicionar token
zoneAPI.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para lidar com erros
zoneAPI.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Não invalidar token se estivermos no período de graça após login
      if (isInLoginGracePeriod()) {
        console.warn('[ZoneAPI] 401 durante período de graça após login, ignorando invalidação de token');
        return Promise.reject(error);
      }
      
      const isLoginPage = window.location.pathname === '/login';
      if (!isLoginPage) {
        const currentToken = localStorage.getItem('token');
        if (currentToken) {
          localStorage.removeItem('token');
          localStorage.removeItem('default_company_id');
          window.dispatchEvent(new CustomEvent('auth:token-invalid'));
        }
      }
    }
    return Promise.reject(error);
  }
);

export interface Zone {
  id: string;
  name: string;
  description?: string;
  boundaries?: string; // JSON string com coordenadas do polígono
  center_lat: number;
  center_lon: number;
  radius?: number; // Raio em km (para zonas circulares)
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateZoneRequest {
  name: string;
  description?: string;
  boundaries?: string;
  center_lat: number;
  center_lon: number;
  radius?: number;
}

export interface UpdateZoneRequest {
  name?: string;
  description?: string;
  boundaries?: string;
  center_lat?: number;
  center_lon?: number;
  radius?: number;
  is_active?: boolean;
}

export interface ZoneAssignmentRequest {
  latitude: number;
  longitude: number;
}

export interface ZoneAssignmentResponse {
  zone_id: string;
  zone_name: string;
  distance: number; // Distância em km
}

export interface ZoneStats {
  zone_id: string;
  total_properties: number;
  total_employees: number;
}

/**
 * Lista todas as zonas
 */
export const getAllZones = async (activeOnly: boolean = false): Promise<Zone[]> => {
  const response = await zoneAPI.get('/zones', {
    params: { active_only: activeOnly }
  });
  return response.data.zones || [];
};

/**
 * Busca uma zona pelo ID
 */
export const getZoneById = async (zoneId: string): Promise<Zone> => {
  const response = await zoneAPI.get(`/zones/${zoneId}`);
  return response.data;
};

/**
 * Cria uma nova zona
 */
export const createZone = async (data: CreateZoneRequest): Promise<Zone> => {
  const response = await zoneAPI.post('/zones', data);
  return response.data;
};

/**
 * Atualiza uma zona
 */
export const updateZone = async (zoneId: string, data: UpdateZoneRequest): Promise<Zone> => {
  const response = await zoneAPI.put(`/zones/${zoneId}`, data);
  return response.data;
};

/**
 * Deleta (desativa) uma zona
 */
export const deleteZone = async (zoneId: string): Promise<void> => {
  await zoneAPI.delete(`/zones/${zoneId}`);
};

/**
 * Designa automaticamente uma zona baseada em coordenadas geográficas
 */
export const assignZoneByLocation = async (
  request: ZoneAssignmentRequest
): Promise<ZoneAssignmentResponse> => {
  const response = await zoneAPI.post('/zones/assign', request);
  return response.data;
};

/**
 * Busca estatísticas de uma zona
 */
export const getZoneStats = async (zoneId: string): Promise<ZoneStats> => {
  const response = await zoneAPI.get(`/zones/${zoneId}/stats`);
  return response.data;
};

export default zoneAPI;






